import React, { useCallback, useRef, useState } from 'react';
import type { FormEvent, ChangeEvent } from 'react';
import cn from 'classnames';
import MagnifierMIcon from '@alfalab/icons-glyph/MagnifierMIcon';
import { AddMIcon } from '@alfalab/icons-glyph/AddMIcon';
import { useClickAway } from 'react-use';
import { Stack } from '@alfa-bank/site-stack';

import styles from '../../styles/styles.css';
import { goToSearch } from '../../helpers';
import { SuggestBlock } from '../suggests-block';

import type { TYandexSearchBar } from './models';

export const YandexSearchBarBlack = ({
    onSearchIconClick,
    placeholder,
    searchId,
    searchUrl,
    setShowSuggests,
    showSuggests,
    suggestList,
    suggestTitle,
}: TYandexSearchBar) => {
    const [input, setInput] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const yandexSearchBarRef = useRef<HTMLInputElement>(null);
    const yandexSearchContainerRef = useRef<HTMLDivElement>(null);

    const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setInput(event.target.value);
    }, []);

    const handleSubmit = useCallback(
        (event: FormEvent) => {
            event.preventDefault();
            if (input.trim() === '') return;
            goToSearch(input, searchId, searchUrl);
        },
        [input, searchId, searchUrl]
    );
    const yaActive = () => {
        if (onSearchIconClick) {
            onSearchIconClick();

            return;
        } else if (showSuggests) return;
        setShowSuggests(true);
        yandexSearchBarRef.current?.focus();
    };

    useClickAway(yandexSearchContainerRef, () => {
        setShowSuggests(false);
    });

    const secondaryDeleteHandler = useCallback(() => {
        if (input) {
            setInput('');
        }

        yandexSearchBarRef.current?.focus({ preventScroll: false });
    }, [input]);

    return (
        <div
            ref={yandexSearchContainerRef}
            className={cn(styles.yandexSearchContainer, { [styles.isActive]: showSuggests })}
            data-test-id="test-ya-search"
        >
            <form action="" className={styles.yaForm} onSubmit={handleSubmit}>
                <div className={styles.yaBar}>
                    <Stack>
                        {zIndex => (
                            <button
                                className={styles.openSearchButton}
                                data-test-id="test-ya-button"
                                onClick={yaActive}
                                style={{ zIndex }}
                                type="button"
                            >
                                <MagnifierMIcon cursor="pointer" />
                            </button>
                        )}
                    </Stack>
                    <input
                        ref={yandexSearchBarRef}
                        className={styles.search}
                        data-test-id={'test-ya-input'}
                        onBlur={() => {
                            setIsFocused(false);
                        }}
                        onChange={handleChange}
                        onFocus={() => {
                            setIsFocused(true);
                        }}
                        placeholder={placeholder}
                        type="search"
                        value={input}
                    />
                    <AddMIcon
                        className={cn(styles.external, styles.secondaryDeleteIcon, {
                            [styles.hidden]: !isFocused || !input,
                        })}
                        data-test-id={'test-ya-cross'}
                        onClick={secondaryDeleteHandler}
                    />

                    {suggestList?.length && showSuggests && (
                        <SuggestBlock suggestList={suggestList} suggestTitle={suggestTitle} />
                    )}
                </div>
            </form>
        </div>
    );
};
